import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.css';
import { getApplicationFigure } from './applicationFigures';
import { usePhotoFigure } from './usePhotoFigure';

// Deslocamento mínimo (px na tela) para um clique virar traço
const DRAG_THRESHOLD = 4;

// Tamanhos na tela (px), convertidos para unidades do quadro a cada medição
const POINT_RADIUS = 9;
const MUTED_RADIUS = 6;
const RING_RADIUS = 13;
const STROKE_WIDTH = 3;
const ARROW_SIZE = 10;
const NUMBER_FONT = 11;
const LABEL_FONT = 11;
const LABEL_OFFSET = 14;
// Área invisível na ponta da seta para arrastar só a direção do traço
const END_HANDLE_RADIUS = 10;

const pct = (fraction) => `${fraction * 100}%`;
const clamp01 = (value) => Math.min(1, Math.max(0, value));

/** Triângulo da ponta do traço, apontando de (x1, y1) para (x2, y2). */
function arrowPoints(x1, y1, x2, y2, size) {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const left = angle + Math.PI * 0.8;
  const right = angle - Math.PI * 0.8;
  return [
    `${x2},${y2}`,
    `${x2 + Math.cos(left) * size},${y2 + Math.sin(left) * size}`,
    `${x2 + Math.cos(right) * size},${y2 + Math.sin(right) * size}`,
  ].join(' ');
}

/**
 * Figura do mapa de aplicação (rosto, frente ou costas) com as marcações
 * desenhadas em SVG: círculo numerado na cor do produto no ponto (ou no
 * início do traço), traço com seta na direção do movimento e, se
 * `showQuantities`, a quantidade ao lado ("4 UI").
 *
 * `marks`: [{ key, kind, x, y, x2, y2, color, number, label, muted, selected,
 * highlighted, title, details }] (veja `toFigureMarks`), em frações do quadro.
 * Com `enabled`: clique cria um ponto (`onPoint({ x, y })`) e arrastar cria um
 * traço (`onStroke(start, end)`), em frações; `draftColor` pinta a prévia.
 * `onMarkClick(key)` e `onMarkHover(key | null)` atendem às marcações.
 * `onMarkMove(key, delta, part)` recebe o arrasto de uma marcação: `part`
 * 'all' move ponto ou traço inteiro, 'end' só a ponta da seta.
 * `background` ({ attachmentId, crop } ou null) troca o desenho pela foto do
 * paciente recortada; as frações passam a valer sobre o recorte.
 */
const ApplicationFigure = ({
  view,
  variant,
  marks = [],
  enabled = false,
  showQuantities = true,
  draftColor = '#5A80FB',
  onPoint,
  onStroke,
  onMarkClick,
  onMarkHover,
  onMarkMove,
  background = null,
  large = false,
}) => {
  const drawing = getApplicationFigure(view, variant);
  const photo = usePhotoFigure(background);
  // Quadro visível: o recorte da foto ou o desenho inteiro
  const figure = photo.figure || { ...drawing, viewBox: { x: 0, y: 0, width: drawing.width, height: drawing.height } };
  const frame = figure.viewBox;
  const frameHeight = frame.height;
  const waiting = !!background && !photo.figure;
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const moveRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [moving, setMoving] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  // Unidades do quadro por pixel na tela: mantém círculos, textos e traços no mesmo tamanho visual
  const [unitsPerPixel, setUnitsPerPixel] = useState(frameHeight / 520);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const rect = svg.getBoundingClientRect();
      if (rect.height > 0) setUnitsPerPixel(frameHeight / rect.height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [frameHeight, view, waiting]);

  function pointFromEvent(event) {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: clamp01((event.clientX - rect.left) / rect.width),
      y: clamp01((event.clientY - rect.top) / rect.height),
      clientX: event.clientX,
      clientY: event.clientY,
    };
  }

  function handlePointerDown(event) {
    if (!enabled || event.button !== 0) return;
    const point = pointFromEvent(event);
    dragRef.current = { start: point, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event) {
    const move = moveRef.current;
    if (move) {
      const point = pointFromEvent(event);
      if (!move.moved) {
        const distance = Math.hypot(point.clientX - move.start.clientX, point.clientY - move.start.clientY);
        if (distance < DRAG_THRESHOLD) return;
        move.moved = true;
      }
      setMoving({ key: move.key, part: move.part, dx: point.x - move.start.x, dy: point.y - move.start.y });
      return;
    }
    const drag = dragRef.current;
    if (!drag) return;
    const point = pointFromEvent(event);
    if (!drag.moved) {
      const distance = Math.hypot(point.clientX - drag.start.clientX, point.clientY - drag.start.clientY);
      if (distance < DRAG_THRESHOLD) return;
      drag.moved = true;
    }
    setDraft({ start: drag.start, end: point });
  }

  function handlePointerUp(event) {
    const move = moveRef.current;
    if (move) {
      moveRef.current = null;
      setMoving(null);
      const point = pointFromEvent(event);
      if (move.moved) {
        if (onMarkMove) onMarkMove(move.key, { dx: point.x - move.start.x, dy: point.y - move.start.y }, move.part);
      } else if (onMarkClick) {
        onMarkClick(move.key);
      }
      return;
    }
    const drag = dragRef.current;
    dragRef.current = null;
    setDraft(null);
    if (!drag) return;
    const point = pointFromEvent(event);
    if (drag.moved) {
      if (onStroke) onStroke({ x: drag.start.x, y: drag.start.y }, { x: point.x, y: point.y });
    } else if (onPoint) {
      onPoint({ x: drag.start.x, y: drag.start.y });
    }
  }

  function handlePointerCancel() {
    dragRef.current = null;
    moveRef.current = null;
    setDraft(null);
    setMoving(null);
  }

  // Começa a arrastar uma marcação; o clique sem movimento vira seleção no pointerup
  function handleMarkPointerDown(event, mark, part) {
    event.stopPropagation();
    if (!enabled || mark.muted || event.button !== 0) return;
    moveRef.current = { key: mark.key, part, start: pointFromEvent(event), moved: false };
    svgRef.current.setPointerCapture(event.pointerId);
  }

  function handleMarkEnter(key) {
    setHoverKey(key);
    if (onMarkHover) onMarkHover(key);
  }

  function handleMarkLeave() {
    setHoverKey(null);
    if (onMarkHover) onMarkHover(null);
  }

  const k = unitsPerPixel;
  const toFrame = (fx, fy) => ({ x: frame.x + fx * frame.width, y: frame.y + fy * frame.height });
  const hovered = marks.find((mark) => mark.key === hoverKey) || null;

  function renderMark(mark) {
    const isMoving = moving && moving.key === mark.key;
    const shiftAll = isMoving && moving.part === 'all';
    const shiftEnd = isMoving && moving.part === 'end';
    const start = toFrame(
      clamp01(mark.x + (shiftAll ? moving.dx : 0)),
      clamp01(mark.y + (shiftAll ? moving.dy : 0)),
    );
    const end = mark.kind === 'stroke' && mark.x2 != null
      ? toFrame(
        clamp01(mark.x2 + (shiftAll || shiftEnd ? moving.dx : 0)),
        clamp01(mark.y2 + (shiftAll || shiftEnd ? moving.dy : 0)),
      )
      : null;
    const draggable = enabled && !mark.muted;
    const radius = (mark.muted ? MUTED_RADIUS : POINT_RADIUS) * k;
    const ring = mark.selected || mark.highlighted;
    return (
      <g
        key={mark.key}
        className={[styles.mark, mark.muted ? styles.markMuted : '', draggable ? styles.markDraggable : '', isMoving ? styles.markMoving : ''].join(' ')}
        onPointerDown={(event) => handleMarkPointerDown(event, mark, 'all')}
        onClick={(event) => { event.stopPropagation(); if (!draggable && onMarkClick) onMarkClick(mark.key); }}
        onPointerEnter={() => handleMarkEnter(mark.key)}
        onPointerLeave={handleMarkLeave}
      >
        {end && (
          <>
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke="transparent" strokeWidth={14 * k} />
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke={mark.color} strokeWidth={STROKE_WIDTH * k} strokeLinecap="round" />
            <polygon points={arrowPoints(start.x, start.y, end.x, end.y, ARROW_SIZE * k)} fill={mark.color} />
            {draggable && (
              <circle
                cx={end.x}
                cy={end.y}
                r={END_HANDLE_RADIUS * k}
                fill="transparent"
                className={styles.markEndHandle}
                onPointerDown={(event) => handleMarkPointerDown(event, mark, 'end')}
              />
            )}
          </>
        )}
        {ring && (
          <circle
            cx={start.x}
            cy={start.y}
            r={RING_RADIUS * k}
            fill="none"
            stroke={mark.selected ? mark.color : 'var(--color-blue-light3)'}
            strokeWidth={2 * k}
          />
        )}
        <circle cx={start.x} cy={start.y} r={radius} fill={mark.color} stroke="#FFFFFF" strokeWidth={1.5 * k} />
        {!mark.muted && (
          <text
            x={start.x}
            y={start.y}
            className={styles.markNumber}
            fontSize={NUMBER_FONT * k}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {mark.number}
          </text>
        )}
        {!mark.muted && showQuantities && mark.label && (
          <text
            x={start.x + LABEL_OFFSET * k}
            y={start.y}
            className={styles.markLabel}
            fontSize={LABEL_FONT * k}
            strokeWidth={3 * k}
            dominantBaseline="central"
          >
            {mark.label}
          </text>
        )}
      </g>
    );
  }

  const draftStart = draft ? toFrame(draft.start.x, draft.start.y) : null;
  const draftEnd = draft ? toFrame(draft.end.x, draft.end.y) : null;

  if (waiting) {
    return (
      <div className={styles.figureBox}>
        <div className={[styles.figurePlaceholder, large ? styles.figureLarge : ''].join(' ')}>
          <span>{photo.error || 'Carregando foto...'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.figureBox}>
      <svg
        ref={svgRef}
        viewBox={`${frame.x} ${frame.y} ${frame.width} ${frame.height}`}
        className={[styles.figure, large ? styles.figureLarge : '', enabled ? styles.figureInteractive : ''].join(' ')}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        role={enabled ? 'button' : 'img'}
        aria-label="Figura do mapa de aplicação"
      >
        <image href={figure.src} x="0" y="0" width={figure.width} height={figure.height} className={figure.photo ? styles.photo : styles.image} />
        {marks.map(renderMark)}
        {draft && (
          <g className={styles.draft}>
            <line x1={draftStart.x} y1={draftStart.y} x2={draftEnd.x} y2={draftEnd.y} stroke={draftColor} strokeWidth={STROKE_WIDTH * k} strokeLinecap="round" strokeDasharray={`${6 * k} ${5 * k}`} />
            <polygon points={arrowPoints(draftStart.x, draftStart.y, draftEnd.x, draftEnd.y, ARROW_SIZE * k)} fill={draftColor} />
            <circle cx={draftStart.x} cy={draftStart.y} r={POINT_RADIUS * k} fill={draftColor} stroke="#FFFFFF" strokeWidth={1.5 * k} />
          </g>
        )}
      </svg>

      {hovered && !draft && !moving && (
        <div
          className={`${styles.markTip} ${hovered.y < 0.18 ? styles.markTipBelow : ''}`}
          style={{ left: pct(hovered.x), top: pct(hovered.y) }}
        >
          <span className={styles.markTipTitle}>{hovered.title}</span>
          {(hovered.details || []).map((line, index) => (
            <span key={index} className={styles.markTipLine}>{line}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApplicationFigure;
